// Equipment Lab — production free-play surface for every Tier 2 simulator.
//
// The Lab is the canonical practice surface for hardware-fidelity panels.
// Modules link operators here via <EquipmentLabLink> instead of embedding
// full simulators inside slide content (which collapses the layout).
//
// Index route shows a crew chip row + card grid; clicking a card opens the
// single-sim view at /lab/:kind. Phase 2 wires scenario picker, Phase 3
// adds the docked walkthrough panel.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SIMULATORS, SIMULATOR_KINDS, type SimulatorKind } from "../components/simulators";

type CrewFilter = "all" | "audio" | "video" | "stage" | "it";

const CREW_CHIPS: { id: CrewFilter; label: string }[] = [
  { id: "all", label: "All crews" },
  { id: "video", label: "Video" },
  { id: "audio", label: "Audio" },
  { id: "it", label: "IT" },
  { id: "stage", label: "Stage" },
];

export default function EquipmentLab() {
  const [crew, setCrew] = useState<CrewFilter>("all");

  const visible = useMemo<SimulatorKind[]>(
    () =>
      SIMULATOR_KINDS.filter(
        (kind) => crew === "all" || SIMULATORS[kind].crew === crew,
      ),
    [crew],
  );

  return (
    <div className="lab-index">
      <header className="crew-header">
        <p className="eyebrow">Equipment Lab</p>
        <h1>Practice the gear</h1>
        <p className="lead">
          Every controller you'll touch in the booth, free-play. No scoring,
          no timer — just the panel and the multiview, at full size, for as
          long as you need.
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Filter by crew"
        className="lab-crew-chips"
      >
        {CREW_CHIPS.map((chip) => (
          <button
            key={chip.id}
            role="tab"
            type="button"
            aria-selected={crew === chip.id}
            className="lab-crew-chip"
            onClick={() => setCrew(chip.id)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="lab-empty">
          No simulators registered for this crew yet.
        </p>
      ) : (
        <div className="lab-grid">
          {visible.map((kind) => {
            const entry = SIMULATORS[kind];
            return (
              <Link
                key={kind}
                to={`/lab/${kind}`}
                className="portal-card lab-card"
                aria-label={`Open ${entry.label} in the Equipment Lab`}
              >
                <span
                  className="lab-card-crew"
                  data-crew={entry.crew}
                  aria-hidden="true"
                >
                  {entry.crew}
                </span>
                <span className="portal-title">{entry.label}</span>
                <span className="portal-desc">{entry.hardware}</span>
              </Link>
            );
          })}
        </div>
      )}

      <style>{LAB_INDEX_STYLES}</style>
    </div>
  );
}

const LAB_INDEX_STYLES = `
.lab-crew-chips {
  display: flex; gap: 8px; flex-wrap: wrap;
  margin: 1.5rem 0 1rem;
}
.lab-crew-chip {
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #d4cdb8;
  background: transparent;
  font-size: 13px; letter-spacing: 0.5px;
  color: #3a352b;
  cursor: pointer;
}
.lab-crew-chip[aria-selected="true"] {
  background: #2a261d;
  color: #f5efde;
  border-color: #2a261d;
}
.lab-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 1rem;
}
.lab-card { position: relative; }
.lab-card-crew {
  position: absolute; top: 12px; right: 14px;
  font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase;
  font-weight: 700; color: #6e6452;
}
.lab-card-crew[data-crew="video"] { color: #c46a1f; }
.lab-card-crew[data-crew="audio"] { color: #2d6a4f; }
.lab-card-crew[data-crew="stage"] { color: #5a4a8a; }
.lab-card-crew[data-crew="it"] { color: #8a4a6a; }
.lab-empty {
  margin-top: 2rem; color: #6e6452; font-style: italic;
}
`;
